import { FragmentType, gql } from '@/gql';
import { useAuth } from '@/hooks/Authentication/auth';
import { NotificationsTopics } from '@/hooks/Notifications/utils/NotificationsTopics';
import { createContext } from '@/utils/react-utils';
import { useQuery } from '@apollo/client';
import { useFiltersByScreen } from '@/components_v2/Filtersv3/filter-store';
import { reduceNestedFilters } from '@/utils/filters';
import React from 'react';

export const importFragment = gql(`
  fragment ImportFragment on Import {
    id
    name
    progress
    status
    token
    updatedAt
    lastModifiedAt
    fileTemporaryUrl
    importType
    importErrors
    importWithErrors
    extraParams
    createdAt
    arrayErrors
    wrongCountColumns
    exceededMaxRows
    deletedAt
    dataContent
    createOrigin
    authorId
    author {
      id
      email
      fullName
    }
  }
`);

export const importsQuery = gql(`
  query Imports($after: String, $before: String, $first: Int, $last: Int, $filters: JSON, $importType: [String!]) {
    imports(after: $after, before: $before, first: $first, last: $last, filters: $filters, importType: $importType) {
      edges {
        node {
          id,
          ...ImportFragment
        }
        cursor
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
    }
  }
`);

export type ImportFragmentType = FragmentType<typeof importFragment>;

export function useImportsController() {
  const { user } = useAuth();
  const { byKey } = useFiltersByScreen();

  // Memoize filter processing to avoid recalculation on every render
  const processedFilters = React.useMemo(() => {
    const reduced = reduceNestedFilters(byKey);

    // Extract parameters that go outside filters
    const { importType, ...otherFilters } = reduced;

    // Handle created_at filter like in Tickets screen
    const cleanFilters = { ...otherFilters };
    const createdAtFilter = cleanFilters.created_at
      ? JSON.parse(cleanFilters.created_at)
      : {};
    delete cleanFilters.created_at;

    // Add created_at to imports nested structure
    if (createdAtFilter.created_at) {
      cleanFilters.imports = {
        ...cleanFilters.imports,
        created_at: createdAtFilter.created_at,
      };
    }

    return {
      cleanFilters,
      importType,
    };
  }, [byKey]);

  const { cleanFilters, importType } = processedFilters;

  // Memoize query variables to prevent unnecessary refetches
  const queryVariables = React.useMemo(
    () => ({
      first: 50,
      filters: cleanFilters,
      importType,
    }),
    [cleanFilters, importType],
  );

  const { data, fetchMore, loading, refetch, variables } = useQuery(
    importsQuery,
    {
      skip: !user?.id,
      fetchPolicy: 'cache-and-network',
      variables: queryVariables,
      notifyOnNetworkStatusChange: true,
    },
  );

  // Memoize notification subscription to avoid recreating on every render
  React.useEffect(() => {
    const unsub = NotificationsTopics.subscribeTopic('Import', () => {
      refetch(queryVariables);
    });

    return () => {
      unsub();
    };
  }, [refetch, queryVariables]);

  const pageInfo = React.useMemo(() => data?.imports.pageInfo, [
    data?.imports.pageInfo,
  ]);

  const onFetchMore = React.useCallback(() => {
    if (!pageInfo?.hasNextPage) return;

    fetchMore({
      variables: {
        after: pageInfo.endCursor,
        first: 50,
      },
    });
  }, [fetchMore, pageInfo?.endCursor, pageInfo?.hasNextPage]);

  const imports = React.useMemo(
    () => data?.imports?.edges?.map((e: any) => e.node) || [],
    [data?.imports?.edges],
  );

  return {
    onFetchMore,
    pageInfo,
    loading,
    imports,
  };
}

type ImportsContextvalue = ReturnType<typeof useImportsController>;

export const [ImportContext, useImports] = createContext<ImportsContextvalue>();

export function ImportsProvider({ children }: { children?: React.ReactNode }) {
  return (
    <ImportContext value={useImportsController()}>{children}</ImportContext>
  );
}
